import React from 'react';

import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import RepeatButton from '../../../components/repeat-button';
import { useIsDocumentUploadEnabled } from '../../../feature-flags/hooks/use-is-document-upload-enabled';
import { useCustomsDocuments } from '../../../orders/order-details/fulfillment-plan/sections/customs-documents/use-customs-documents';
import { DocumentUploadForm } from '../../../orders/order-details/fulfillment-plan/sections/customs-documents/document-upload-form';
import { messages } from '../../../orders/order-details/fulfillment-plan/sections/customs-documents/messages';
import style from './customs-documents-modal-upload-section.module.less';

type CustomsDocumentsModalUploadSectionProps = {
  shipmentId: string;
  fulfillmentPlanId?: string;
};

/**
 * Post-ship customs document upload section rendered inside the Shipment Details modal.
 * Shown when a label has been purchased (shipmentId exists).
 */
export const CustomsDocumentsModalUploadSection = ({ shipmentId, fulfillmentPlanId }: CustomsDocumentsModalUploadSectionProps) => {
  const isDocumentUploadEnabled = useIsDocumentUploadEnabled();
  // Hook must be called unconditionally (Rules of Hooks). Overhead is acceptable
  // since selectors are cheap and the hook short-circuits internally on missing IDs.
  const {
    isLoadingCapabilities,
    isCarrierSupported,
    availableDocumentTypes,
    carrierCodeForUpload,
    trackingNumber,
    salesOrderId,
    orderDocuments,
    isSending,
    handleRefresh,
    handleSendToCarrier,
  } = useCustomsDocuments({
    shipmentId,
    fulfillmentPlanId,
  });

  if (!isDocumentUploadEnabled || !shipmentId) return null;
  if (isLoadingCapabilities) return null;

  return (
    <FlexColumn className={style['modal-upload-section']} gap={Gap.Space12}>
      <FlexRow className={style['section-header']} gap={Gap.Space8}>
        <Text textType={TextType.H4} value={messages.sectionTitle} />
        <RepeatButton
          actionHandler={handleRefresh}
          ariaLabelMessageDescriptor={messages.refreshAriaLabel}
          className={style['refresh-icon']}
        />
      </FlexRow>
      <DocumentUploadForm
        availableDocumentTypes={availableDocumentTypes}
        carrierCode={carrierCodeForUpload}
        extraDocuments={orderDocuments}
        initialState='ready'
        isDisabled={!isCarrierSupported}
        isSending={isSending}
        onSubmit={handleSendToCarrier}
        salesOrderId={salesOrderId}
        shipmentId={shipmentId}
        trackingNumber={trackingNumber}
      />
    </FlexColumn>
  );
};
